/**
 * src/components/mfa/useReauth.tsx
 * 設定変更（パスキー登録/削除・認証方式の変更・ポリシー変更など）に必要な再認証を共通化する。
 *
 * 使い方:
 *   const { withReauth, dialog } = useReauth()
 *   await withReauth((extra) => mfaFetch('/auth/mfa/method', 'PUT', { method: 'passkey', ...extra }))
 *   ... return (<> ... {dialog} </>)
 *
 * サーバーが STEPUP_REQUIRED を返したときだけ、必要な再認証（パスキー / メールコード /
 * パスワード / システム保守担当者の登録コード）のダイアログを出し、成功したら元の操作を自動でやり直す。
 * 判断（どの再認証が必要か）は常にサーバーが行う。
 */

import { useCallback, useEffect, useState } from 'react'
import { MfaApiError, mfaFetch, type ReauthKind } from '../../api/mfa'
import MfaChallenge from './MfaChallenge'

type Extra = { current_password?: string }

export function RecoveryTokenForm({ onSuccess, submitLabel = '確認する' }: {
  onSuccess: () => void
  submitLabel?: string
}) {
  const [token, setToken] = useState('')
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState<string | null>(null)

  const submit = async (e: React.FormEvent) => {
    e.preventDefault()
    setError(null)
    setBusy(true)
    try {
      await mfaFetch('/auth/mfa/recovery', 'POST', { token })
      onSuccess()
    } catch (err) {
      setError(err instanceof MfaApiError ? err.message : '確認に失敗しました。')
    } finally {
      setBusy(false)
    }
  }

  return (
    <form onSubmit={submit}>
      <label style={{ display: 'block', fontSize: 13, fontWeight: 600, color: 'var(--ink-2)', marginBottom: 6 }}>
        システム保守担当者が発行した登録コード
      </label>
      <input
        value={token} onChange={(e) => setToken(e.target.value)} autoComplete="off" spellCheck={false}
        placeholder="発行されたコードを貼り付け" disabled={busy}
        style={{
          display: 'block', width: '100%', boxSizing: 'border-box', height: 46, padding: '0 12px',
          fontSize: 14, fontFamily: 'var(--font-mono)', background: 'var(--paper)', color: 'var(--ink)',
          border: '1.5px solid var(--paper-3)', borderRadius: 8, outline: 'none',
        }}
      />
      <div style={{ fontSize: 12, color: 'var(--ink-3)', lineHeight: 1.7, marginTop: 8 }}>
        このコードは使い捨てで、発行から15分で無効になります。発行手順は「パスキー紛失時の復旧マニュアル」を参照してください。
      </div>
      {error && (
        <div role="alert" style={{ background: 'var(--red-light)', color: 'var(--red)', borderRadius: 8, padding: '10px 14px', fontSize: 13, marginTop: 12 }}>
          ⚠ {error}
        </div>
      )}
      <button type="submit" className="btn btn-primary" disabled={busy || token.trim().length < 20}
        style={{ display: 'block', width: '100%', height: 46, fontSize: 15, fontWeight: 600, borderRadius: 10, marginTop: 12 }}>
        {busy ? '確認中...' : submitLabel}
      </button>
    </form>
  )
}

const TITLES: Record<ReauthKind, string> = {
  passkey: 'パスキーで本人確認',
  email: 'メールコードで本人確認',
  password: 'パスワードで本人確認',
  recovery_token: '登録コードで本人確認',
}

function ReauthDialog({ kind, onDone, onCancel }: {
  kind: ReauthKind
  onDone: (extra: Extra) => void
  onCancel: () => void
}) {
  const [password, setPassword] = useState('')
  const [emailMasked, setEmailMasked] = useState<string | null>(null)

  useEffect(() => {
    if (kind !== 'email') return
    mfaFetch('/auth/mfa/status').then((s) => setEmailMasked(s.email_masked ?? null)).catch(() => {})
  }, [kind])

  return (
    <div className="overlay" style={{ zIndex: 1300 }} onClick={(e) => e.target === e.currentTarget && onCancel()}>
      <div className="modal" style={{ maxWidth: 440 }} role="dialog" aria-modal="true" aria-label={TITLES[kind]}>
        <div className="modal-header">
          <div className="modal-title">🔒 {TITLES[kind]}</div>
          <button className="btn btn-ghost btn-sm" onClick={onCancel} aria-label="閉じる">✕</button>
        </div>
        <div className="modal-body">
          <p style={{ fontSize: 13, color: 'var(--ink-2)', lineHeight: 1.7, marginTop: 0 }}>
            セキュリティのため、この操作の前に本人確認が必要です。
          </p>
          {kind === 'passkey' && <MfaChallenge method="passkey" mode="stepup" onSuccess={() => onDone({})} onSessionLost={() => window.location.replace('/login')} />}
          {kind === 'email' && <MfaChallenge method="email" mode="stepup" emailMasked={emailMasked} onSuccess={() => onDone({})} onSessionLost={() => window.location.replace('/login')} />}
          {kind === 'recovery_token' && <RecoveryTokenForm onSuccess={() => onDone({})} />}
          {kind === 'password' && (
            <form onSubmit={(e) => { e.preventDefault(); if (password) onDone({ current_password: password }) }}>
              <label style={{ display: 'block', fontSize: 13, fontWeight: 600, color: 'var(--ink-2)', marginBottom: 6 }}>現在のパスワード</label>
              <input aria-label="現在のパスワード" type="password" autoFocus value={password} onChange={(e) => setPassword(e.target.value)}
                autoComplete="current-password"
                style={{
                  display: 'block', width: '100%', boxSizing: 'border-box', height: 46, padding: '0 14px', fontSize: 16,
                  background: 'var(--paper)', color: 'var(--ink)', border: '1.5px solid var(--paper-3)', borderRadius: 8, outline: 'none',
                }} />
              <button type="submit" className="btn btn-primary" disabled={!password}
                style={{ display: 'block', width: '100%', height: 46, fontSize: 15, fontWeight: 600, borderRadius: 10, marginTop: 12 }}>
                続ける
              </button>
            </form>
          )}
        </div>
      </div>
    </div>
  )
}

export function useReauth() {
  const [req, setReq] = useState<{ kind: ReauthKind; resolve: (v: Extra | null) => void } | null>(null)

  const withReauth = useCallback(async <T,>(fn: (extra: Extra) => Promise<T>): Promise<T> => {
    let extra: Extra = {}
    for (let attempt = 0; attempt < 3; attempt++) {
      try {
        return await fn(extra)
      } catch (e) {
        if (e instanceof MfaApiError && e.code === 'STEPUP_REQUIRED') {
          const kind = (e.data.reauth as ReauthKind) ?? 'password'
          const got = await new Promise<Extra | null>((resolve) => setReq({ kind, resolve }))
          setReq(null)
          if (!got) throw new MfaApiError(0, { error: '操作をキャンセルしました。', code: 'CANCELLED' })
          extra = got
          continue
        }
        throw e
      }
    }
    throw new MfaApiError(0, { error: '本人確認を完了できませんでした。もう一度お試しください。', code: 'REAUTH_FAILED' })
  }, [])

  const dialog = req ? (
    <ReauthDialog kind={req.kind} onDone={(x) => req.resolve(x)} onCancel={() => req.resolve(null)} />
  ) : null

  return { withReauth, dialog }
}
